import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  busGetIndBusAtList,
  busGetPendingKyc,
  busKycSubmit,
  busKycHistory,
  busVerifyBusPan,
  busVerifyBusGstin,
  updateErrorStatus,
  setLinkExpired,
} from "../../redux/slices/EnqMngtKycSlice";
import {
  getCompleteKycLink,
  getOnboardingStepStatus,
} from "../../redux/slices/QuestionnaireSlice";
import { setActiveCategory } from "../../redux/slices/dealCategorySlice";
import { getOrgDealCategories } from "../../redux/slices/AuthSlice";
import { Form, Typography, Checkbox, message, Tag, Spin, Card, Result } from "antd";
import CustomButton from "../../components/CustomButton/Button";
import BackButton from "../../components/CustomButton/BackButton";
import KycBusinessDetails from "./Components/KycBusinessDetails";
import { TITLE_TXT, BTN_TXT, GLOBAL_CONSTANTS } from "../../constants/constants";
import KycAddress from "./Components/KycAddress";
import KycBank from "./Components/KycBank";
import KycAdditionalInfo from "./Components/KycAdditionalInfo";
import KycContactDirector from "./Components/KycContactDirector";
import KycSuccess from "./Components/KycSuccess";
import FormFooter from "../../components/CustomForms/FormFooter";
import dayjs from "dayjs";
import {
  isCommunicationSameAsPrimary,
  buildCommunicationAddressPayload,
} from "../../helpers/kycAddress";
import {
  getKycStatusLabel,
  getKycStatusTagColor,
  getEffectiveKycStatusName,
  isCompleteKycReadOnly,
  isKycSubmittedStatus,
  shouldShowKycSuccessScreen,
} from "../../helpers/kycStatus";
import "./CompleteKyc.scss";

const { Text } = Typography;

const parseCompleteKycUrl = (url = "") => {
  try {
    const parsed = new URL(url);
    return {
      token: parsed.searchParams.get("token"),
      orgId: parsed.searchParams.get("orgId"),
    };
  } catch {
    return { token: null, orgId: null };
  }
};

const cleanNa = (val) => {
  if (!val) return "";
  const lower = String(val).toLowerCase();
  return lower === "n/a" ? "" : val;
};

const isTokenExpiredClient = (token) => {
  if (!token) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(jsonPayload);
    if (payload.exp && Date.now() >= payload.exp * 1000) {
      return true;
    }
    if (payload.lastLoginAt) {
      const loginAt = new Date(payload.lastLoginAt).getTime();
      const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
      if (!isNaN(loginAt) && Date.now() - loginAt >= TWENTY_FOUR_HOURS_MS) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

const buildCompleteKycFormValues = (pendingKycData) => {
  if (!pendingKycData) return {};

  const kyc = pendingKycData.kycDetails || {};
  const bank = pendingKycData.BankDetailsView || {};

  const verifiedPan =
    kyc.kycPanValue?.isVerified && kyc.kycPanValue?.docValue
      ? kyc.kycPanValue.docValue
      : null;
  const verifiedGstin =
    kyc.kycGstinValue?.isVerified && kyc.kycGstinValue?.docValue
      ? kyc.kycGstinValue.docValue
      : null;
  const verifiedBank =
    kyc.kycBank?.isVerified && kyc.kycBank?.docValue
      ? kyc.kycBank.docValue
      : null;
  const verifiedDirAadhaar =
    kyc.kycDirectorsAadhaarValue?.isVerified &&
    kyc.kycDirectorsAadhaarValue?.docValue
      ? kyc.kycDirectorsAadhaarValue.docValue
      : null;
  const verifiedDirPan =
    kyc.kycDirectorsPanValue?.isVerified && kyc.kycDirectorsPanValue?.docValue
      ? kyc.kycDirectorsPanValue.docValue
      : null;

  return {
    busName: pendingKycData.busName,
    busPan: verifiedPan || pendingKycData.pan || kyc.kycPanValue?.docValue,
    busType: pendingKycData.businessTypeId,
    incorporationDate: pendingKycData.incorporationDate
      ? dayjs(pendingKycData.incorporationDate)
      : null,
    industryType: pendingKycData.industryTypeId,
    annualTurnover: pendingKycData.annualTurnoverId,
    hasMultipleGstin: pendingKycData.hasMultipleGstin,
    gstin: verifiedGstin || pendingKycData.gstin || kyc.kycGstinValue?.docValue,
    primaryAdd1: pendingKycData.primaryAddLine1,
    primaryAdd2: pendingKycData.primaryAddLine2,
    primaryCity: pendingKycData.primaryCityName,
    primaryState: pendingKycData.primaryStateName,
    primaryPincode: pendingKycData.primaryZipCode,
    commAdd1: cleanNa(pendingKycData.billingAddLine1),
    commAdd2: cleanNa(pendingKycData.billingAddLine2),
    commCity: cleanNa(pendingKycData.billingCityName),
    commState: cleanNa(pendingKycData.billingStateName),
    commPincode: cleanNa(pendingKycData.billingZipCode),
    commSameAsPrimary: isCommunicationSameAsPrimary(pendingKycData),
    email: pendingKycData.email,
    phoneNo: pendingKycData.contactPhoneNumber,
    ifsc: bank.ifscCode,
    bankName: bank.bankName,
    branchName: bank.branchName,
    accHolderName: cleanNa(bank.accHolderName) || pendingKycData.busName || "",
    accNum: verifiedBank || bank.accNum || kyc.kycBank?.docValue,
    tenure: pendingKycData.expectedTenure?.toString(),
    expAmt: pendingKycData.expectedCredit,
    busWebsite: pendingKycData.busWebsite?.replace(/^https?:\/\//i, ""),
    dealType: pendingKycData.dealCategoryId,
    busDesc: pendingKycData.busDescription,
    finSummary: pendingKycData.financialSummary,
    dirName: cleanNa(pendingKycData.directorName),
    dirAadhaar: verifiedDirAadhaar || kyc.kycDirectorsAadhaarValue?.docValue,
    dirPan: verifiedDirPan || kyc.kycDirectorsPanValue?.docValue,
  };
};

const CompleteKyc = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");
  const organizationId = searchParams.get("orgId");
  const { user } = useSelector((state) => state.auth);
  const { activeCategory } = useSelector((state) => state.dealCategory);
  const {
    pendingKycData,
    pendingKycSubmitLoading,
    error,
    panError,
    gstinError,
    panLoading,
    gstinLoading,
    kycHistoryData,
    loading,
    isLinkExpired,
  } = useSelector((state) => state?.enqMngt);
  const isExpired = Boolean(isLinkExpired) || Boolean(token && isTokenExpiredClient(token));
  const formValues = Form.useWatch(undefined, form);
  const { busPan, busName, gstin, email, phoneNo, ifsc, accNum, accHolderName } =
    formValues || {};
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(!token);

  const mergedKycDetails =
    kycHistoryData?.kycDetails || pendingKycData?.kycDetails || {};
  const isPanVerified = mergedKycDetails?.kycPanValue?.isVerified;
  const isGstinVerified = mergedKycDetails?.kycGstinValue?.isVerified;
  const effectiveStatusName = getEffectiveKycStatusName({
    statusName: pendingKycData?.statusName,
    isSubmitted,
    hasToken: Boolean(token),
  });
  const kycStatusLabel = getKycStatusLabel({
    kycDetails: mergedKycDetails,
    statusName: effectiveStatusName,
  });
  const kycStatusColor = getKycStatusTagColor(kycStatusLabel);
  const isReadOnly = isCompleteKycReadOnly({
    statusName: effectiveStatusName,
    isSubmitted,
    hasToken: Boolean(token),
  });
  const showKycSuccess = shouldShowKycSuccessScreen({
    statusName: effectiveStatusName,
    isSubmitted,
    hasToken: Boolean(token),
  });
  const isAlreadyCompleted = Boolean(
    token &&
      isKycSubmittedStatus(pendingKycData?.statusName) &&
      !isSubmitted,
  );

  const goToQuestionnaire = useCallback(
    (options = {}) => {
      navigate("/questionnaire", {
        replace: true,
        state: options.kycSuccess ? { kycSuccess: true } : undefined,
      });
    },
    [navigate],
  );

  const goToQuestionnaireAfterSuccess = useCallback(() => {
    goToQuestionnaire({ kycSuccess: true });
  }, [goToQuestionnaire]);

  const refreshOnboardingState = useCallback(async () => {
    const dealCategoryId =
      activeCategory?.dealCategoryId || user?.dealCategories?.[0]?.dealCategoryId;

    if (user?.orgId && dealCategoryId) {
      await dispatch(
        getOnboardingStepStatus({
          orgId: user.orgId,
          dealCategoryId,
        }),
      );
    }

    // Refresh Redux user.dealCategories so ProtectedRoute does not
    // overwrite the verified activeCategory with a stale KYC_PENDING status.
    await dispatch(getOrgDealCategories());

    const verifiedStatusId = GLOBAL_CONSTANTS.STAT_KYC_VERIFIED_ID;
    if (activeCategory) {
      dispatch(
        setActiveCategory({
          ...activeCategory,
          statusId: verifiedStatusId,
        }),
      );
    }
  }, [activeCategory, dispatch, user]);

  useEffect(() => {
    const initSession = async () => {
      if (token && organizationId) {
        setSessionLoading(false);
        return;
      }

      setSessionLoading(true);
      const result = await dispatch(getCompleteKycLink());

      if (getCompleteKycLink.fulfilled.match(result) && result.payload?.completeKycUrl) {
        const parsed = parseCompleteKycUrl(result.payload.completeKycUrl);
        if (parsed.token && parsed.orgId) {
          setSearchParams(
            { token: parsed.token, orgId: parsed.orgId },
            { replace: true },
          );
          setSessionLoading(false);
          return;
        }
      }

      setSessionLoading(false);
      goToQuestionnaire();
    };

    initSession();
  }, [dispatch, goToQuestionnaire, organizationId, setSearchParams, token]);

  useEffect(() => {
    if (!token || !organizationId) return;

    if (isTokenExpiredClient(token)) {
      dispatch(setLinkExpired(true));
      return;
    }

    dispatch(busGetIndBusAtList({ token }));
    dispatch(busGetPendingKyc({ orgId: organizationId, token }));
    dispatch(busKycHistory({ orgId: organizationId, token }));

    return () => {
      dispatch(updateErrorStatus());
    };
  }, [dispatch, organizationId, token]);

  useEffect(() => {
    if (pendingKycData) {
      form.setFieldsValue(buildCompleteKycFormValues(pendingKycData));
    }
  }, [pendingKycData, form]);

  useEffect(() => {
    const directorDetails = kycHistoryData?.kycDetails || pendingKycData?.kycDetails;
    const historyKyc = kycHistoryData?.kycDetails;
    const patch = {};

    if (directorDetails?.kycDirectorsAadhaarValue?.isVerified) {
      patch.dirAadhaar = directorDetails.kycDirectorsAadhaarValue?.docValue;
      patch.dirName =
        kycHistoryData?.directorName || pendingKycData?.directorName;
    }

    if (directorDetails?.kycDirectorsPanValue?.isVerified) {
      patch.dirPan = directorDetails.kycDirectorsPanValue?.docValue;
    }

    if (historyKyc?.kycBank?.isVerified && historyKyc.kycBank?.docValue) {
      patch.accNum = historyKyc.kycBank.docValue;
    }

    const bankView =
      pendingKycData?.BankDetailsView || kycHistoryData?.orgDets?.BankDetailsView;
    if (historyKyc?.kycBank?.isVerified && bankView) {
      if (bankView.ifscCode) patch.ifsc = bankView.ifscCode;
      if (bankView.bankName) patch.bankName = bankView.bankName;
      if (bankView.branchName) patch.branchName = bankView.branchName;
      if (bankView.accHolderName) {
        patch.accHolderName = bankView.accHolderName;
      } else if (pendingKycData?.busName) {
        patch.accHolderName = pendingKycData.busName;
      }
    }

    if (historyKyc?.kycPanValue?.isVerified && historyKyc.kycPanValue?.docValue) {
      patch.busPan = historyKyc.kycPanValue.docValue;
    }
    if (historyKyc?.kycGstinValue?.isVerified && historyKyc.kycGstinValue?.docValue) {
      patch.gstin = historyKyc.kycGstinValue.docValue;
    }

    if (Object.keys(patch).length) {
      form.setFieldsValue(patch);
    }
  }, [kycHistoryData, pendingKycData, form]);

  const handleVerifyBoth = async () => {
    let refreshRequired = false;

    if (!isPanVerified) {
      const panResponse = await dispatch(
        busVerifyBusPan({
          busName,
          pan: busPan,
          orgId: organizationId,
          token,
        }),
      );
      if (panResponse?.payload?.statusCode !== 201) {
        return;
      }
      refreshRequired = true;
    }

    if (!isGstinVerified) {
      const gstinResponse = await dispatch(
        busVerifyBusGstin({
          gstin,
          orgId: organizationId,
          token,
        }),
      );
      if (gstinResponse?.payload?.statusCode === 201) {
        refreshRequired = true;
      }
    }

    if (refreshRequired) {
      dispatch(busGetPendingKyc({ orgId: organizationId, token }));
      dispatch(busKycHistory({ orgId: organizationId, token }));
    }
  };

  const handleSubmitKyc = async (values) => {
    const payload = {
      token,
      orgId: organizationId,
      businessTypeId: values.busType,
      industryTypeId: values.industryType,
      hasMultipleGstin: values.hasMultipleGstin,
      annualTurnoverId: values.annualTurnover,
      dealCategoryId: values.dealType,
      expectedTenure: values.tenure,
      expectedCredit: values.expAmt,
      incorporationDate:
        values.incorporationDate || pendingKycData.incorporationDate,
      busDescription: values.busDesc,
      website: values.busWebsite
        ? values.busWebsite.match(/^https?:\/\//i)
          ? values.busWebsite
          : `http://${values.busWebsite}`
        : "",
      financialSummary: values.finSummary,
      communicationAddress: buildCommunicationAddressPayload(values),
    };

    const result = await dispatch(busKycSubmit(payload));

    if (result.payload?.statusCode === 201) {
      setIsSubmitted(true);
      await refreshOnboardingState();
      return;
    }

    message.error(result?.payload?.message || "Failed to submit KYC");
  };

  const handleSaveDraft = async () => {
    setDraftLoading(true);
    const values = form.getFieldsValue();
    const payload = {
      isDraft: true,
      token,
      orgId: organizationId,
      businessTypeId: values.busType,
      industryTypeId: values.industryType,
      hasMultipleGstin: values.hasMultipleGstin,
      annualTurnoverId: values.annualTurnover,
      dealCategoryId: values.dealType,
      expectedTenure: values.tenure ? Number(values.tenure) : undefined,
      expectedCredit: values.expAmt ? Number(values.expAmt) : undefined,
      incorporationDate: values.incorporationDate
        ? values.incorporationDate.format("YYYY-MM-DD")
        : undefined,
      busDescription: values.busDesc,
      website: values.busWebsite,
      financialSummary: values.finSummary,
      dirName: values.dirName,
      accNum: values.accNum,
      accHolderName: values.accHolderName,
      ifsc: values.ifsc,
      communicationAddress: buildCommunicationAddressPayload(values),
    };

    const result = await dispatch(busKycSubmit(payload));
    setDraftLoading(false);

    if (result.payload?.statusCode === 201) {
      message.success("KYC Draft saved successfully");
      dispatch(busGetPendingKyc({ orgId: organizationId, token }));
      dispatch(busKycHistory({ orgId: organizationId, token }));
    } else {
      message.error(result?.payload?.message || "Failed to save KYC Draft");
    }
  };

  const isDirectorKycLink = searchParams.get("directorKyc") === "true";
  const isDirectorAadhaarVerified = mergedKycDetails?.kycDirectorsAadhaarValue?.isVerified;

  if (isExpired || (isDirectorKycLink && isDirectorAadhaarVerified)) {
    return (
      <div className="complete-kyc-container">
        <BackButton onClick={goToQuestionnaire} />
        <Card className="complete-kyc-card" style={{ textAlign: "center", padding: "48px 24px" }}>
          <Result
            status="warning"
            title={isExpired ? "KYC Link Expired" : "Director KYC Already Verified"}
            subTitle={isExpired ? "This link is valid for 24 hours. If it expires, you will need to request a new one." : "The Director KYC has already been successfully verified. This link is no longer accessible."}
          />
        </Card>
      </div>
    );
  }

  if (showKycSuccess) {
    return (
      <KycSuccess
        isAlreadyCompleted={isAlreadyCompleted}
        autoRedirect={!isAlreadyCompleted}
        onContinue={
          isAlreadyCompleted ? goToQuestionnaire : goToQuestionnaireAfterSuccess
        }
      />
    );
  }

  if (sessionLoading || (loading && !pendingKycData?.busName)) {
    return <Spin fullscreen />;
  }

  return (
    <div className="complete-kyc-container">
      <BackButton onClick={goToQuestionnaire} />
      <div className="kyc-page-header">
        <Text className="kyc-title">
          KYC Details for{" "}
          <span style={{ fontWeight: "bold" }}>{pendingKycData?.busName}</span>
        </Text>
        <Tag color={kycStatusColor} className="kyc-status-tag">
          {kycStatusLabel}
        </Tag>
      </div>
      <Card className="complete-kyc-card">
        <Form layout="vertical" form={form} onFinish={handleSubmitKyc}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Text className="kyc-subtitle">{TITLE_TXT.BUS_DETS}</Text>
            {!isReadOnly && (!isPanVerified || !isGstinVerified) && (
              <CustomButton
                variant="outlined"
                color="green"
                htmlType="button"
                onClick={handleVerifyBoth}
                loading={panLoading || gstinLoading}
              >
                {`Verify ${isPanVerified ? "GSTIN" : "Both PAN & GSTIN"}`}
              </CustomButton>
            )}
          </div>
          <KycBusinessDetails
            kycData={mergedKycDetails?.kycPanValue}
            busPan={busPan}
            busName={busName}
            organizationId={organizationId}
            token={token}
            isReview={isReadOnly}
          />
          <KycContactDirector
            kycData={mergedKycDetails}
            email={email}
            phoneNo={phoneNo}
            form={form}
            organizationId={organizationId}
            token={token}
            dealCategoryId={
              formValues?.dealType || pendingKycData?.dealCategoryId
            }
            isReview={isReadOnly}
          />
          <KycBank
            kycData={mergedKycDetails}
            ifsc={ifsc}
            accNum={accNum}
            accHolderName={accHolderName}
            form={form}
            organizationId={organizationId}
            token={token}
            isReview={isReadOnly}
          />
          <Text className="kyc-subtitle">{TITLE_TXT.ADD_VERIFY}</Text>
          <KycAddress
            kycData={mergedKycDetails?.kycGstinValue}
            gstin={gstin}
            form={form}
            organizationId={organizationId}
            token={token}
            isReview={isReadOnly}
          />
          <KycAdditionalInfo isReview={isReadOnly} />
          {!isReadOnly && (
            <>
              <Form.Item style={{ marginTop: 20 }}>
                <Checkbox
                  checked={isTermsAccepted}
                  onChange={(e) => setIsTermsAccepted(e.target.checked)}
                >
                  I confirm that I have verified and uploaded all the information
                  provided for the company {busName}.
                </Checkbox>
              </Form.Item>
              <FormFooter
                onCancelClick={goToQuestionnaire}
                noCancel
                error={error || panError || gstinError}
                submitLoading={pendingKycSubmitLoading}
                btnName={BTN_TXT.SUBMIT}
                disabled={!isTermsAccepted}
                draftBtn="Save as Draft"
                onDraftClick={handleSaveDraft}
                draftLoading={draftLoading}
              />
            </>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default CompleteKyc;
